import { Dispatch, SetStateAction, useState } from 'react';
import { View } from 'react-native';

import {
  IconInfoFill,
  ImageAssetWithAltText,
  ListItemCheckbox,
  ListItemGroup,
  type ListItemGroupProps,
  ListItemGroupRadioGroup,
  ListItemGroupSection,
  ListItemLinkPressable,
  ListItemQuantity,
  ListItemStepper,
  ListItemType,
  Text,
  XStack,
  createMqStyles,
  tokens,
} from '@fhs/ui';
import { useDebouncedCallback } from '@fhs/utils';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { useToast } from '@fhs-legacy/native-base';

import { useMenuPicker } from '../state/menu-picker';
import type { CustomizationSelections, MenuItemCustomizationOptionGroup } from '../types';
import {
  formatCaloriesOffsetText,
  formatCentsToDollarsWithPlusSign,
  formatCustomizationAdditionalCostLabel,
} from '../utils';
import { useCustomizationLimits } from '../utils/use-customization-limits';

import { PickerActionSheet } from './picker-action-sheet';

type CustomizationGroupDataType = MenuItemCustomizationOptionGroup;

type CustomizationGroupDisplayType = 'RADIO' | 'QUANTITY' | 'MIXED';

type CustomizationGroupOption = CustomizationGroupDataType['options'][number];

function getCustomizationGroupDisplayType(
  customizationGroup: CustomizationGroupDataType
): CustomizationGroupDisplayType {
  if (customizationGroup.options.length === 1) {
    const optionDisplayType = customizationGroup.options[0].displayType;
    if (optionDisplayType === 'RADIO' || optionDisplayType === 'QUANTITY') {
      return optionDisplayType;
    }
  }

  return 'MIXED';
}

export type CustomizationGroupProps = Omit<ListItemGroupProps, 'items'> & {
  customizationGroup: CustomizationGroupDataType;
  customizationSelections: CustomizationSelections;
  setCustomizationSelections: Dispatch<SetStateAction<CustomizationSelections>>;
};

export function CustomizationGroup({
  customizationGroup,
  customizationSelections,
  setCustomizationSelections,
  ...listItemGroupProps
}: CustomizationGroupProps) {
  const { showErrors, errorMessages, ...menuPicker } = useMenuPicker();

  const [pickerActionSheetVisibility, setPickerActionSheetVisibility] = useState<
    Record<string, boolean>
  >({});
  const displayType = getCustomizationGroupDisplayType(customizationGroup);

  const isCustomizationLimitEnabled = useFlag('enable-customization-limits');

  const { isDisabled, maxAllowed } = useCustomizationLimits(
    customizationGroup,
    customizationSelections,
    isCustomizationLimitEnabled
  );

  const toast = useToast();

  const { logRBIEvent } = useCRMEventsContext();

  const fireAmplitudeEvent = useDebouncedCallback(
    (customizationOption: CustomizationGroupOption) => {
      const selections = {
        ...menuPicker.userCustomizationSelections,
        ...menuPicker.draftCustomizationSelections,
      };

      logRBIEvent({
        name: CustomEventNames.CUSTOMIZATION_LIMIT_EXCEEDED,
        type: EventTypes.Other,
        attributes: {
          pickerSlug: menuPicker.slug,
          selectedPickerAspectOptionId: menuPicker.selectedPickerAspectOptionId ?? '',
          customizationDisplayGroupDisplayName: customizationGroup.displayGroup.displayName ?? '',
          targetCustomizationOptionDisplayName: customizationOption.displayName ?? '',
          maxAllowedCustomizations: maxAllowed,
          selectedCustomizationsInDisplayGroup: customizationGroup.options
            .filter(o => selections[o.key] && selections[o.key] !== o.defaultOptionKey)
            .map(o => o.displayName),
        },
      });
    },
    1000
  );

  const createSoftDisabledPressHandler = (customizationOption: CustomizationGroupOption) => () => {
    if (!maxAllowed) {
      return;
    }

    fireAmplitudeEvent(customizationOption);

    toast.show({
      render: () => <ToastWrapper maxAllowed={maxAllowed} />,
    });
  };

  const listGroupItems: ListItemGroupProps['items'] = customizationGroup.options.map(
    customizationOption => {
      return {
        id: customizationOption.key,
        render: (listItemProps: ListItemType) => {
          switch (customizationOption.displayType) {
            case 'CHECKBOX': {
              const onOption = customizationOption.options.find(
                o => o.multiplier && o.multiplier > 0
              );
              const offOption = customizationOption.options.find(o => o.multiplier === 0);
              const checked = customizationSelections[customizationOption.key] === onOption?.key;

              return (
                <ListItemCheckbox
                  {...listItemProps}
                  checked={checked}
                  onChange={nextState => {
                    return setCustomizationSelections(prev => ({
                      ...prev,
                      [customizationOption.key]: nextState
                        ? onOption?.key ?? ''
                        : offOption?.key ?? '',
                    }));
                  }}
                  image={onOption?.image as ImageAssetWithAltText}
                  title={customizationOption.displayName ?? ''}
                  subtitle={formatCaloriesOffsetText(onOption?.caloriesOffset)}
                  indicatorText={
                    formatCentsToDollarsWithPlusSign(onOption?.upChargeCents ?? 0) ?? ''
                  }
                  indicatorAccessibilityLabel={
                    formatCustomizationAdditionalCostLabel(onOption?.upChargeCents ?? 0) ??
                    undefined
                  }
                  unavailable={!onOption?.isAvailable} // if option is unavailable, set it to disable
                  softDisabled={isDisabled(customizationOption.key)}
                  onSoftDisabledPress={createSoftDisabledPressHandler(customizationOption)}
                />
              );
            }

            case 'SELECT': {
              const currentValue = customizationSelections[customizationOption.key];
              const selectedOption = customizationOption.options.find(o => o.key === currentValue);
              if (!selectedOption) {
                return null;
              }

              return (
                <>
                  <ListItemLinkPressable
                    {...listItemProps}
                    onPress={() =>
                      setPickerActionSheetVisibility(visibility => ({
                        ...visibility,
                        [customizationOption.key]: true,
                      }))
                    }
                    image={selectedOption.image as ImageAssetWithAltText}
                    title={customizationOption.displayName ?? ''}
                    subtitle={[
                      formatCentsToDollarsWithPlusSign(selectedOption.upChargeCents),
                      selectedOption.displayName,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                    disabled={isDisabled(customizationOption.key)}
                  />
                  <PickerActionSheet
                    headerTitle="Choose Your Bread" // In the future, we may need this title to be dynamic. Ideally the BE should provide this field for us.
                    isVisible={Boolean(pickerActionSheetVisibility[customizationOption.key])}
                    onClose={() =>
                      setPickerActionSheetVisibility(visibility => ({
                        ...visibility,
                        [customizationOption.key]: false,
                      }))
                    }
                    selectedOptionKey={selectedOption.key}
                    onSelect={optionSelected => {
                      setCustomizationSelections(selections => ({
                        ...selections,
                        [customizationOption.key]: optionSelected?.key ?? '',
                      }));
                    }}
                    options={customizationOption.options}
                  />
                </>
              );
            }

            case 'STEPPER': {
              const currentValue = customizationSelections[customizationOption.key] as string; // forcing to string because stepper will never have quantities
              const selectedOption = customizationOption.options.find(o => o.key === currentValue);

              const availableOptions = customizationOption.options.filter(o => o.isAvailable);
              const hasAvailableOption = availableOptions.some(
                o => o.isAvailable && o.prefix !== 'No'
              );

              if (!selectedOption) {
                return null;
              }

              const caloriesOffset = formatCaloriesOffsetText(selectedOption?.caloriesOffset);
              const upsellPrice = formatCentsToDollarsWithPlusSign(
                selectedOption?.upChargeCents ?? 0
              );
              const subtitle = upsellPrice ? `${upsellPrice} • ${caloriesOffset}` : caloriesOffset;

              return (
                <ListItemStepper
                  {...listItemProps}
                  options={availableOptions.map(o => ({
                    label: o.prefix ?? o.displayName,
                    value: o.key,
                  }))}
                  value={currentValue}
                  onChangeValue={nextValue => {
                    setCustomizationSelections(selections => ({
                      ...selections,
                      [customizationOption.key]: nextValue,
                    }));
                  }}
                  image={selectedOption?.image as ImageAssetWithAltText}
                  title={customizationOption.displayName ?? ''}
                  subtitle={subtitle}
                  unavailable={!hasAvailableOption}
                  incrementSoftDisabled={isDisabled(customizationOption.key)}
                  onIncrementSoftDisabledPress={createSoftDisabledPressHandler(customizationOption)}
                />
              );
            }

            default: {
              return null;
            }
          }
        },
      };
    }
  );
  const showLimitMessage = isCustomizationLimitEnabled && maxAllowed;
  switch (displayType) {
    case 'MIXED': {
      return (
        <ListItemGroupSection heading={customizationGroup.displayGroup.displayName}>
          {showLimitMessage && <Text.Ui size="sm"> Select up to {maxAllowed} options</Text.Ui>}
          <ListItemGroup {...listItemGroupProps} items={listGroupItems} />
        </ListItemGroupSection>
      );
    }

    case 'QUANTITY': {
      const customizationOption = customizationGroup.options[0];

      const quantityState = customizationSelections[customizationOption.key] as Record<
        string,
        number
      >;
      const totalSelectedQuantity = Object.values(quantityState).reduce(
        (total, curr) => total + curr,
        0
      );

      const sectionDescription =
        customizationOption.minAmount &&
        customizationOption.maxAmount &&
        customizationOption.minAmount !== customizationOption.maxAmount
          ? `Choose ${customizationOption.minAmount} to ${customizationOption.maxAmount} options`
          : `Choose ${customizationOption.minAmount} options`;

      const errorMessage = showErrors && errorMessages[customizationOption.key];

      const remainingQuantity = (customizationOption.maxAmount ?? 0) - totalSelectedQuantity;

      return (
        <ListItemGroupSection
          heading={customizationGroup.displayGroup.displayName}
          description={sectionDescription}
          errorMessage={errorMessage || ''}
        >
          <ListItemGroup
            {...listItemGroupProps}
            errorMessage={errorMessage || ''}
            items={customizationOption.options.map(opt => ({
              id: opt.id,
              render: (listItemProps: ListItemType) => (
                <ListItemQuantity
                  {...listItemProps}
                  title={opt.displayName}
                  subtitle={
                    opt.caloriesOffset != null ? `${opt.caloriesOffset} Cal` : undefined
                  }
                  indicatorText={formatCentsToDollarsWithPlusSign(opt.upChargeCents) ?? ''}
                  label={[
                    opt.displayName,
                    formatCustomizationAdditionalCostLabel(opt.upChargeCents),
                    opt.caloriesOffset != null ? `${opt.caloriesOffset} Cal` : null,
                  ]
                    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
                    .join('. ')}
                  image={opt.image as ImageAssetWithAltText}
                  minValue={0}
                  maxValue={remainingQuantity + (quantityState[opt.key] || 0)}
                  value={quantityState[opt.key]}
                  onChangeValue={nextAmount =>
                    setCustomizationSelections(selections => ({
                      ...selections,
                      [customizationOption.key]: {
                        ...quantityState,
                        [opt.key]: nextAmount,
                      },
                    }))
                  }
                  disabled={!opt.isAvailable || isDisabled(opt.key)}
                />
              ),
            }))}
          />
        </ListItemGroupSection>
      );
    }

    case 'RADIO': {
      const customizationOption = customizationGroup.options[0];
      const value = customizationSelections[customizationOption.key] as string; // forcing to string because radio will never have quantities

      return (
        <ListItemGroupSection heading={customizationGroup.displayGroup.displayName}>
          <ListItemGroupRadioGroup
            {...listItemGroupProps}
            value={value}
            onChange={nextValue =>
              setCustomizationSelections(selections => ({
                ...selections,
                [customizationOption.key]: nextValue,
              }))
            }
            options={customizationOption.options.map(opt => ({
              value: opt.key,
              title: opt.displayName ?? '',
              subtitle: formatCaloriesOffsetText(opt.caloriesOffset),
              indicatorText: formatCentsToDollarsWithPlusSign(opt.upChargeCents) ?? '',
              indicatorAccessibilityLabel:
                formatCustomizationAdditionalCostLabel(opt.upChargeCents) ?? undefined,
              image: opt.image as ImageAssetWithAltText,
              disabled: !opt.isAvailable || isDisabled(opt.key),
            }))}
          />
        </ListItemGroupSection>
      );
    }
  }
}

const useMqStyles = createMqStyles({
  softDisabledMessage: {
    $base: {
      marginLeft: 8,
      color: 'white',
      textAlign: 'center',
      flexShrink: 1,
    },
  },
  contentContainer: {
    $base: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 358,
      height: 56,
      gap: 8,
      borderRadius: 4,
      padding: 16,
    },
  },
  toast: {
    $base: {
      backgroundColor: tokens.colors.$houseDark,
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: 8,
      maxWidth: '100%',
    },
  },
});

const ToastWrapper = ({ maxAllowed }: { maxAllowed: number }) => {
  const mqStyles = useMqStyles();

  return (
    <View style={mqStyles.contentContainer}>
      <XStack style={mqStyles.toast}>
        <IconInfoFill size={25} color={'white'} />
        <Text.Ui size={'md'} weight="normal" style={mqStyles.softDisabledMessage}>
          You can select a maximum of {maxAllowed} option{maxAllowed > 1 && 's'}
        </Text.Ui>
      </XStack>
    </View>
  );
};
